import { FilterOutlined, LoadingOutlined } from "@ant-design/icons";
import { Button, Collapse, Drawer, Space, Tag, Typography } from "antd";
import { useMediaQuery } from "@mui/material";
import React from "react";
import { useAppTranslation } from "~/hooks/useAppTranslation";

export type ResponsiveFilterChip = {
  key: string;
  label: React.ReactNode;
  value?: React.ReactNode;
};

export interface ResponsiveFilterPanelProps {
  title?: React.ReactNode;
  activeFilters?: ResponsiveFilterChip[];
  isLoading?: boolean;
  children: React.ReactNode;
  desktopCollapseKey?: string;
  desktopDefaultOpen?: boolean;
  mobileDrawerTitle?: React.ReactNode;
  mobileTriggerLabel?: React.ReactNode;
  mobileBreakpointQuery?: string;
  mobileDrawerWidth?: number | string;
  mobileApplyLabel?: React.ReactNode;
  mobileClearLabel?: React.ReactNode;
  onApply?: () => void;
  onClear?: () => void;
  closeDrawerOnApply?: boolean;
  closeDrawerOnClear?: boolean;
}

const renderChipValue = (chip: ResponsiveFilterChip) => {
  if (chip.value === undefined || chip.value === null || chip.value === "") {
    return chip.label;
  }
  return (
    <>
      {chip.label}: {chip.value}
    </>
  );
};

export const ResponsiveFilterPanel = ({
  title,
  activeFilters = [],
  isLoading = false,
  children,
  desktopCollapseKey = "responsive-filter-panel",
  desktopDefaultOpen = false,
  mobileDrawerTitle,
  mobileTriggerLabel,
  mobileBreakpointQuery = "(max-width: 768px)",
  mobileDrawerWidth = "100%",
  mobileApplyLabel,
  mobileClearLabel,
  onApply,
  onClear,
  closeDrawerOnApply = true,
  closeDrawerOnClear = false,
}: ResponsiveFilterPanelProps) => {
  const { t } = useAppTranslation();
  const isMobile = useMediaQuery(mobileBreakpointQuery);
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  const chipList =
    activeFilters.length > 0 ? (
      <Space size={[8, 8]} wrap>
        {activeFilters.map((chip) => (
          <Tag color="green" key={chip.key}>
            {renderChipValue(chip)}
          </Tag>
        ))}
      </Space>
    ) : null;

  const headerContent = (
    <Space size={[8, 8]} wrap>
      {title && <Typography.Text>{title}</Typography.Text>}
      {chipList}
      {isLoading && <LoadingOutlined spin />}
    </Space>
  );

  if (!isMobile) {
    return (
      <Collapse
        size="small"
        defaultActiveKey={desktopDefaultOpen ? [desktopCollapseKey] : []}
        items={[
          {
            key: desktopCollapseKey,
            label: headerContent,
            children,
          },
        ]}
      />
    );
  }

  const triggerCount = activeFilters.length;
  const resolvedTriggerLabel = mobileTriggerLabel ?? title ?? t("Filters");
  const resolvedDrawerTitle = mobileDrawerTitle ?? title ?? t("Filters");
  const resolvedApplyLabel = mobileApplyLabel ?? t("Apply_filters");
  const resolvedClearLabel = mobileClearLabel ?? t("Clear_filters");

  return (
    <>
      <Space direction="vertical" size={12} style={{ display: "flex", width: "100%" }}>
        <Button
          type="default"
          icon={isLoading ? <LoadingOutlined spin /> : <FilterOutlined />}
          onClick={() => setDrawerOpen(true)}
          block
        >
          {resolvedTriggerLabel}
          {triggerCount > 0 ? ` (${triggerCount})` : ""}
        </Button>
        {chipList}
      </Space>

      <Drawer
        title={resolvedDrawerTitle}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        placement="right"
        width={mobileDrawerWidth}
        styles={{ wrapper: { maxWidth: "100vw" } }}
        footer={
          onApply || onClear ? (
            <Space wrap style={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
              {onClear && (
                <Button
                  onClick={() => {
                    onClear();
                    if (closeDrawerOnClear) {
                      setDrawerOpen(false);
                    }
                  }}
                >
                  {resolvedClearLabel}
                </Button>
              )}
              {onApply && (
                <Button
                  type="primary"
                  loading={isLoading}
                  onClick={() => {
                    onApply();
                    if (closeDrawerOnApply) {
                      setDrawerOpen(false);
                    }
                  }}
                >
                  {resolvedApplyLabel}
                </Button>
              )}
            </Space>
          ) : null
        }
      >
        {children}
      </Drawer>
    </>
  );
};
